import { ref, computed, Ref } from 'vue';
import { ISelectableItem } from '../domains/entities/InventoryTemplate';

/**
 * useTemplateSelectableItemsの戻り値の型
 */
export interface UseTemplateSelectableItemsReturn<T extends ISelectableItem> {
  /** 項目リスト */
  items: Ref<T[]>;
  /** 選択された項目（選択順でソート済み） */
  selectedItems: Ref<T[]>;
  /** 項目のチェック状態を変更 */
  handleItemChange: (item: T, checked: boolean) => void;
  /** 項目の選択順を入れ替え */
  reorderItems: (draggedKey: string, targetKey: string) => void;
  /** 単一フィールド項目の挿入位置を設定 */
  moveSingleFieldItem: (itemKey: string, insertBeforeKey: string | undefined) => void;
  /** 選択をリセット */
  resetSelection: () => void;
}

/**
 * テンプレート機能用の選択可能な項目リストを管理するコンポーザブル
 * @param initialItems - 初期項目リスト
 * @returns 項目管理用の関数群
 */
export const useTemplateSelectableItems = <T extends ISelectableItem>(initialItems: T[]): UseTemplateSelectableItemsReturn<T> => {
  // 初期値をディープコピーして使用
  const items = ref<T[]>(initialItems.map((item) => ({ ...item }))) as Ref<T[]>;
  let selectionCounter = 0;

  /**
   * 選択された項目を選択順でソートして返す
   */
  const selectedItems = computed(() => {
    return items.value.filter((item) => item.checked).sort((a, b) => (a.selectionOrder ?? 0) - (b.selectionOrder ?? 0));
  });

  /**
   * 項目の選択順を更新
   * @param item - 対象の項目
   * @param isChecked - チェック状態
   */
  const updateSelectionOrder = (item: T, isChecked: boolean): void => {
    if (isChecked) {
      if (item.selectionOrder === undefined) {
        item.selectionOrder = selectionCounter++;
      }
    } else {
      const removedOrder = item.selectionOrder;
      if (removedOrder !== undefined) {
        items.value.forEach((i) => {
          if (i.selectionOrder !== undefined && i.selectionOrder > removedOrder) {
            i.selectionOrder! -= 1;
          }
        });
        selectionCounter--;
        item.selectionOrder = undefined;
      }
    }
  };

  /**
   * 項目のチェック状態を変更
   * @param item - 対象の項目
   * @param checked - チェック状態
   */
  const handleItemChange = (item: T, checked: boolean): void => {
    item.checked = checked;
    updateSelectionOrder(item, checked);
  };

  /**
   * 項目の選択順を入れ替え
   * @param draggedKey - ドラッグされた項目のキー
   * @param targetKey - ドロップ先の項目のキー
   */
  const reorderItems = (draggedKey: string, targetKey: string): void => {
    if (draggedKey === targetKey) return;

    const draggedItem = items.value.find((item) => item.key === draggedKey);
    const targetItem = items.value.find((item) => item.key === targetKey);

    if (!draggedItem || !targetItem) return;

    const oldOrder = draggedItem.selectionOrder ?? 0;
    const newOrder = targetItem.selectionOrder ?? 0;

    if (oldOrder === newOrder) return;

    // 他のアイテムの選択順を更新
    items.value.forEach((item) => {
      if (item.selectionOrder !== undefined && item.key !== draggedKey) {
        if (oldOrder < newOrder) {
          // 後ろに移動
          if (item.selectionOrder > oldOrder && item.selectionOrder <= newOrder) {
            item.selectionOrder -= 1;
          }
        } else {
          // 前に移動
          if (item.selectionOrder >= newOrder && item.selectionOrder < oldOrder) {
            item.selectionOrder += 1;
          }
        }
      }
    });

    draggedItem.selectionOrder = newOrder;
  };

  /**
   * 単一フィールド項目の挿入位置を設定
   * @param itemKey - 対象の項目キー
   * @param insertBeforeKey - 挿入位置（この項目の前に挿入、undefinedの場合は末尾）
   */
  const moveSingleFieldItem = (itemKey: string, insertBeforeKey: string | undefined): void => {
    const item = items.value.find((i) => i.key === itemKey);
    if (item) {
      item.insertBeforeKey = insertBeforeKey;
    }
  };

  /**
   * 選択をリセット
   */
  const resetSelection = (): void => {
    items.value.forEach((item) => {
      item.checked = false;
      item.selectionOrder = undefined;
      item.insertBeforeKey = undefined;
    });
    selectionCounter = 0;
  };

  return {
    items,
    selectedItems,
    handleItemChange,
    reorderItems,
    moveSingleFieldItem,
    resetSelection,
  };
};
